import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchPhoneticTree } from "@/lib/api";

const JLPT_COLOR = {
  N5: "#4A6D7C", N4: "#6E7A5A", N3: "#D29C4C", N2: "#B86C45", N1: "#8B4546",
};

export default function PhoneticTree({ ch, onSelect }) {
  const q = useQuery({
    queryKey: ["phonetic-tree", ch],
    queryFn: () => fetchPhoneticTree(ch),
    enabled: !!ch,
  });

  if (q.isLoading || !q.data) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sumiSoft animate-pulse">
          loading phonetic kin…
        </p>
      </div>
    );
  }

  const branches = q.data.branches || [];

  if (branches.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="font-serif text-5xl text-sumi mb-3">{ch}</p>
        <p className="font-sans text-sm text-sumiSoft max-w-md mx-auto">
          {q.data.meaning} doesn&apos;t belong to any phonetic series in this dataset —
          no visible sub-component reliably predicts its on&apos;yomi.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="phonetic-tree-container" className="p-4 md:p-6 overflow-y-auto h-full">
      {/* root kanji */}
      <div className="flex flex-col items-center mb-6">
        <div className="font-serif text-6xl md:text-7xl text-sumi leading-none">{ch}</div>
        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sumiSoft mt-2">
          {q.data.meaning}
        </p>
      </div>

      {/* one tree per branch */}
      <div className="space-y-8">
        {branches.map((b, bi) => (
          <motion.div
            key={b.phonetic_component + "-" + b.reading + "-" + bi}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: bi * 0.06 }}
            data-testid={`phonetic-branch-${b.phonetic_component}-${b.reading}`}
            className="border border-borderDefault bg-washi"
          >
            <div className="px-4 py-3 border-b border-borderDefault flex items-center gap-3 bg-shu/5 flex-wrap">
              <div className="flex items-center gap-2">
                {b.is_kanji && b.phonetic_component !== ch ? (
                  // The phonetic driver is itself a kanji with its own card
                  // (寺 for 時/持/侍/詩, 青 for 清/晴/情, 工 for 江/紅/虹…).
                  // Link the big glyph through so a learner can pivot from
                  // "what drives this sound" to "what IS this driver?".
                  // We hide the link when the driver IS the current kanji
                  // (driver-root case — would be a self-link) and when it's
                  // a ghost driver (𢦏, 俞, 龹 — no own card).
                  <Link
                    to={`/kanji/${encodeURIComponent(b.phonetic_component)}`}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                      if (onSelect) {
                        e.preventDefault();
                        onSelect(b.phonetic_component);
                      }
                    }}
                    data-testid={`phonetic-driver-link-${b.phonetic_component}`}
                    title={`open ${b.phonetic_component} card`}
                    className="font-serif text-4xl text-shu leading-none hover:text-shu/70 transition-colors"
                  >
                    {b.phonetic_component}
                  </Link>
                ) : (
                  <span className="font-serif text-4xl text-shu leading-none">
                    {b.phonetic_component}
                  </span>
                )}
                <div className="flex flex-col">
                  <span className="font-sans text-[10px] uppercase tracking-widest text-sumiSoft">
                    phonetic{b.is_ghost_driver ? " · etymological" : ""}
                  </span>
                  {b.component_meaning && (
                    <span className="font-sans text-xs text-sumi">{b.component_meaning}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <span className="font-sans text-[10px] uppercase tracking-widest text-sumiSoft">
                  drives reading
                </span>
                <span className="font-serif text-2xl text-shu">{b.reading}</span>
              </div>
            </div>

            <div className="p-3 md:p-4 flex flex-wrap gap-2 justify-center">
              {b.members.map((m) => {
                const isCurrent = m.char === ch;
                const acc = m.jlpt ? JLPT_COLOR[m.jlpt] : "#7A746D";
                return (
                  <button
                    key={m.char}
                    data-testid={`phonetic-member-${m.char}`}
                    onClick={() => !isCurrent && onSelect?.(m.char)}
                    title={m.meaning}
                    className={`flex flex-col items-center px-3 py-2 border bg-washi transition-colors ${
                      isCurrent ? "" : "hover:border-shu cursor-pointer"
                    }`}
                    style={{ borderColor: isCurrent ? "#C84B31" : acc, borderWidth: isCurrent ? 2 : 1 }}
                  >
                    <span className={`font-serif text-3xl leading-none ${isCurrent ? "text-shu" : "text-sumi"}`}>
                      {m.char}
                    </span>
                    <span className="font-sans text-[9px] uppercase tracking-widest text-sumiSoft mt-1 truncate max-w-[80px]">
                      {m.meaning}
                    </span>
                    {m.jlpt && (
                      <span className="font-sans text-[9px] uppercase tracking-widest mt-0.5"
                            style={{ color: acc }}>{m.jlpt}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
